import React from 'react';
import styled from 'styled-components';
import { Pencil, CheckCircle, X } from 'lucide-react';
import { SpecialPaymentDto, formatCurrency, formatDate, getStatusBadgeColor } from '../../services/specialPaymentsService';
import { hexToRgba } from '../../utils/colorUtils';

interface SpecialPaymentsTableProps {
  payments: SpecialPaymentDto[];
  onEdit: (payment: SpecialPaymentDto) => void;
  onMarkAsPaid: (payment: SpecialPaymentDto) => void;
  onCancel: (payment: SpecialPaymentDto) => void;
}

const TableContainer = styled.div`
  direction: rtl;
  background: white;
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-light);
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHead = styled.thead`
  background: var(--background-secondary);

  th {
    padding: 16px;
    text-align: center;
    font-weight: 600;
    font-size: 14px;
    color: var(--text-primary);
    border-bottom: 2px solid var(--border-color);
    white-space: nowrap;
  }
`;

const TableBody = styled.tbody`
  tr {
    border-bottom: 1px solid var(--border-color);
    transition: background-color 0.2s ease;

    &:hover {
      background: var(--hover-background);
    }

    &:last-child {
      border-bottom: none;
    }
  }

  td {
    padding: 16px;
    font-size: 14px;
    color: var(--text-primary);
    vertical-align: middle;
    text-align: center;
  }
`;

const StatusBadge = styled.span<{ color: string }>`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: var(--border-radius-sm);
  font-size: 12px;
  font-weight: 600;
  color: ${props => props.color};
  background: ${props => hexToRgba(props.color, 0.1)};
  border: 1px solid ${props => hexToRgba(props.color, 0.25)};
  white-space: nowrap;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 4px;
  justify-content: center;
`;

const IconButton = styled.button<{ variant?: 'primary' | 'success' | 'danger' }>`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 6px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  color: ${props => {
    if (props.variant === 'danger') return '#dc2626';
    if (props.variant === 'success') return '#16a34a';
    return 'var(--primary-color)';
  }};

  &:hover {
    background: var(--hover-background);
  }
`;

const AmountCell = styled.td`
  font-weight: 600;
  color: var(--primary-color);
`;

const EmptyState = styled.tr`
  td {
    text-align: center;
    padding: 48px 16px;
    color: var(--text-secondary);
    font-size: 14px;
  }
`;

const SpecialPaymentsTable: React.FC<SpecialPaymentsTableProps> = ({ payments, onEdit, onMarkAsPaid, onCancel }) => {
  if (payments.length === 0) {
    return (
      <TableContainer>
        <Table>
          <TableHead>
            <tr>
              <th>תיאור</th>
              <th>סכום</th>
              <th>סטטוס</th>
              <th>תאריך תשלום</th>
              <th>נוצר על ידי</th>
              <th>פעולות</th>
            </tr>
          </TableHead>
          <TableBody>
            <EmptyState>
              <td colSpan={6}>
                <div>לא נמצאו תשלומים מיוחדים</div>
                <div style={{ fontSize: '12px', marginTop: '8px', opacity: 0.7 }}>
                  לחץ על "הוסף תשלום מיוחד" כדי להתחיל
                </div>
              </td>
            </EmptyState>
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <tr>
            <th>תיאור</th>
            <th>סכום</th>
            <th>סטטוס</th>
            <th>תאריך תשלום</th>
            <th>נוצר על ידי</th>
            <th>פעולות</th>
          </tr>
        </TableHead>
        <TableBody>
          {payments.map(payment => (
            <tr key={payment.id}>
              <td>
                <div>{payment.description}</div>
                {payment.comments && (
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                    {payment.comments}
                  </div>
                )}
              </td>
              <AmountCell>{formatCurrency(payment.amount)}</AmountCell>
              <td>
                <StatusBadge color={getStatusBadgeColor(payment.status)}>
                  {payment.statusName}
                </StatusBadge>
              </td>
              <td>
                {payment.paymentDate ? (
                  <div>
                    <div>{formatDate(payment.paymentDate)}</div>
                    {payment.paymentMethodName && (
                      <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                        {payment.paymentMethodName}
                      </div>
                    )}
                  </div>
                ) : (
                  <span style={{ color: 'var(--text-secondary)' }}>-</span>
                )}
              </td>
              <td>
                <div>{payment.createdByName}</div>
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                  {formatDate(payment.createdAt)}
                </div>
              </td>
              <td>
                <ActionButtons>
                  <IconButton onClick={() => onEdit(payment)} title="עריכה">
                    <Pencil size={16} />
                  </IconButton>
                  {payment.status === 1 && ( // Only for Pending
                    <IconButton variant="success" onClick={() => onMarkAsPaid(payment)} title="סמן כשולם">
                      <CheckCircle size={16} />
                    </IconButton>
                  )}
                  {payment.status !== 3 && ( // Not cancelled
                    <IconButton variant="danger" onClick={() => onCancel(payment)} title="ביטול">
                      <X size={16} />
                    </IconButton>
                  )}
                </ActionButtons>
              </td>
            </tr>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default SpecialPaymentsTable;
